import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { claimSubOpportunity } from '../domains/squads/services/subbing';
import { useAuth } from '../domains/auth/contexts/AuthContext';
import ConfirmationModal from '../domains/squads/components/ConfirmationModal';
import { db } from '../shared/storage/firebase';
import { DEFAULT_TZ, formatTimeInZone, getTimeZoneLabel } from '../shared/utils/timezone';

const CLAIM_SUB_REDIRECT_KEY = 'og_post_login_path';

type ClaimDetails = {
  classTitle: string;
  day: string;
  time: string;
  address: string;
  requestedRole?: string;
  requestedTeacherName?: string;
};

const ClaimSub: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { currentUser } = useAuth();
  const [status, setStatus] = useState<'idle' | 'loading_details' | 'confirm' | 'claiming' | 'done' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [details, setDetails] = useState<ClaimDetails | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const eventId = params.get('eventId') || '';
  const squadId = params.get('squadId') || '';

  const title = useMemo(() => {
    if (!eventId || !squadId) return 'Missing claim details';
    if (status === 'loading_details') return 'Loading class details…';
    if (status === 'confirm') return 'Confirm claim';
    return 'Claiming class…';
  }, [eventId, squadId, status]);

  useEffect(() => {
    // Clear post-login redirect so we don't loop.
    try { sessionStorage.removeItem(CLAIM_SUB_REDIRECT_KEY); } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => {
    const loadDetails = async () => {
      if (!eventId || !squadId) return;
      try {
        setStatus('loading_details');
        setError(null);

        const [evSnap, squadSnap] = await Promise.all([
          getDoc(doc(db, 'squadEvents', eventId)),
          getDoc(doc(db, 'squads', squadId)),
        ]);

        if (!evSnap.exists()) throw new Error('This class no longer exists.');
        const ev: any = evSnap.data();
        const squad: any = squadSnap.exists() ? squadSnap.data() : null;
        const requestId = String(ev?.subRequestId || '').trim();
        const requestSnap = requestId ? await getDoc(doc(db, 'subRequests', requestId)) : null;
        const request: any = requestSnap?.exists() ? requestSnap.data() : null;

        const start: Date = ev?.start?.toDate?.() || new Date(ev?.start || 0);
        const end: Date = ev?.end?.toDate?.() || new Date(ev?.end || 0);
        const timeZone = String(squad?.timezone || ev?.timeZone || DEFAULT_TZ).trim() || DEFAULT_TZ;
        const tzLabel = getTimeZoneLabel(timeZone);

        const classTitle = String(squad?.title || ev?.title || 'Class');
        const day = formatTimeInZone(start, timeZone, 'EEEE, MMMM d, yyyy');
        const time = `${formatTimeInZone(start, timeZone, 'h:mm a')}–${formatTimeInZone(end, timeZone, 'h:mm a')} ${tzLabel}`;
        const address = String(
          squad?.location?.address
            || squad?.address
            || squad?.locationName
            || squad?.schoolName
            || 'Address TBD'
        );

        setDetails({
          classTitle, day, time, address,
          requestedRole: request?.requestedRole === 'co_teacher' ? 'Co-teacher' : request?.requestedRole === 'lead' ? 'Lead teacher' : undefined,
          requestedTeacherName: String(request?.requestedByTeacherName || '').trim() || undefined,
        });
        setStatus('confirm');
        setConfirmOpen(true);
      } catch (e: any) {
        setStatus('error');
        setError(String(e?.message || 'Failed to load class details.'));
      }
    };
    void loadDetails();
  }, [eventId, squadId]);

  const confirmClaim = async () => {
    if (!currentUser?.uid) {
      setStatus('error');
      setError('Please sign in to claim this class.');
      return;
    }
    if (!eventId || !squadId) {
      setStatus('error');
      setError('Missing eventId or squadId.');
      return;
    }
    try {
      setConfirmOpen(false);
      setStatus('claiming');
      setError(null);
      await claimSubOpportunity({ eventId, squadId, claimerTeacherId: currentUser.uid });
      setStatus('done');
      // Give a tiny beat so "done" is visible in case of very fast claims.
      window.setTimeout(() => navigate('/app/squads', { replace: true }), 350);
    } catch (e: any) {
      setStatus('error');
      setError(String(e?.message || 'Failed to claim class.'));
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>{title}</div>
      {status === 'loading_details' && <div>Loading…</div>}
      {status === 'confirm' && details && <div>Review the details below before claiming.</div>}
      {status === 'claiming' && <div>Please wait…</div>}
      {status === 'done' && <div>Claimed. Redirecting…</div>}
      {status === 'error' && (
        <div style={{ color: '#b91c1c' }}>
          {error || 'Something went wrong.'}
        </div>
      )}

      <ConfirmationModal
        isOpen={confirmOpen && status === 'confirm' && !!details}
        onClose={() => {
          setConfirmOpen(false);
          navigate('/app/squads', { replace: true });
        }}
        onConfirm={() => void confirmClaim()}
        title="Claim this substitute opportunity?"
        message={
          details
            ? 'Check your calendar. Are you sure you want to claim this? Once claimed you are responsible for teaching it.'
              + `\n\nCLASS: ${details.classTitle}`
              + `\nDAY: ${details.day}`
              + `\nTIME: ${details.time}`
              + `\nADDRESS: ${details.address}`
              + (details.requestedRole ? `\nROLE: ${details.requestedRole}` : '')
              + (details.requestedTeacherName ? `\nCOVERING FOR: ${details.requestedTeacherName}` : '')
            : 'Check your calendar. Are you sure you want to claim this? Once claimed you are responsible for teaching it.'
        }
        confirmText="Claim"
        cancelText="Cancel"
      />
    </div>
  );
};

export default ClaimSub;
